import { useEffect, useMemo, useState } from 'react';
import { DataError, useList } from '@hana/data';
import { CONTAINER_CLASSES, findPort, searchPorts, type CargoType } from '@hana/domain';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Chip, Input, Modal, Segmented, StateView, Table, type Column, type ScreenState } from '@hana/ui';
import { IconChevronLeft, IconAnchor, IconArrowsExchange, IconBox, IconCalendarEvent, IconChevronDown, IconChevronUp, IconClock, IconCalendarCheck, IconCheck, IconDots, IconFileText, IconFilter, IconHistory, IconPencil, IconPlugConnected, IconRestore, IconSearch, IconShip } from '@tabler/icons-react';
import { useP2PAccess } from './access';
import { SchedList } from './SchedList';
import { PadRateCard } from './P2PPadCard';
import { BOARD_TODAY } from './fixtures';
import { daysLeftLabel, dm, dmHm, usd } from './format';
import { freetimeLabel, searchRates, searchSailings, sellOf, type RateHit, type RateSort, type SellTier } from './p2p';
import type { P2PBuyRate, P2PRate, Sailing } from './types';
import './P2PScreen.i18n';
import s from './p2p.module.css';

interface Lane {
  pol: string;
  pod: string;
  cargo: CargoType;
  days: number;
}
interface Recent extends Lane {
  at: string;
}

const CARGOS = CONTAINER_CLASSES.filter((c): c is (typeof CONTAINER_CLASSES)[number] & { key: CargoType } => c.key !== 'NOR');
const RECENT: readonly Recent[] = [
  { pol: 'VNSGN', pod: 'KRPUS', cargo: 'RF', days: 28, at: '10:12' },
  { pol: 'VNSGN', pod: 'USLAX', cargo: 'DC', days: 28, at: 'Hôm qua' },
  { pol: 'VNSGN', pod: 'NLRTM', cargo: 'RF', days: 28, at: '21/Oct' },
  { pol: 'VNSGN', pod: 'AEJEA', cargo: 'RF', days: 28, at: '20/Oct' },
];
// TODO: "hôm nay" thật khi đổi sang dữ liệu thật; dữ liệu mẫu của board neo ngày 23/Oct.
const TODAY = BOARD_TODAY;
const ms = (iso: string) => new Date(iso).getTime();

const cityOf = (code: string) => findPort(code)?.name ?? code;
const PHONE_PODS = ['KRPUS', 'USLAX', 'JPTYO', 'NLRTM'];
const CONT_DEFAULT: Record<CargoType, string> = { DC: '1×40HC', RF: '1×40RH', OTH: '1×40FR' };

function CargoField({ value, onPick }: { value: CargoType; onPick: (c: CargoType) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className={s.ctl}>
      <span className={s.ctlLb}><IconBox size={14} aria-hidden="true" />{t('p2.cargo')}</span>
      <div className={s.cargo} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}>
        <button type="button" className={s.cargoBtn} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(!open)}>
          {value} · {CONT_DEFAULT[value]}<IconChevronDown size={14} aria-hidden="true" />
        </button>
        {open && (
          <div className={s.sug} role="listbox" aria-label={t('p2.cargoType')}>
            {CARGOS.map((c) => (
              <button key={c.key} type="button" role="option" aria-selected={c.key === value} className={s.sugBtn} onClick={() => { onPick(c.key); setOpen(false); }}>
                <b>{c.key}</b>
                <span>{c.label}</span>
                <span>{CONT_DEFAULT[c.key]}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const cargoLabel = (c: CargoType) => CARGOS.find((x) => x.key === c)?.label ?? c;

function PortField({ label, aria, value, onPick }: { label: string; aria: string; value: string; onPick: (code: string) => void }) {
  const t = useT();
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? (value ? `${value} · ${cityOf(value)}` : '');
  const sug = text !== null ? searchPorts(text).slice(0, 6) : [];
  return (
    <div className={s.port}>
      <span className={s.ctlLb}><IconAnchor size={14} aria-hidden="true" />{label}</span>
      <Input
        aria-label={aria}
        icon={<IconAnchor size={16} aria-hidden="true" />}
        value={shown}
        placeholder={t('p2.portPh')}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => setTimeout(() => setText(null), 120)}
        autoComplete="off"
      />
      {sug.length > 0 && (
        <div className={s.sug} role="listbox" aria-label={t('p2.suggest', { v: aria.toLowerCase() })}>
          {sug.map((p) => (
            <button
              key={p.code}
              type="button"
              role="option"
              aria-selected={p.code === value}
              className={s.sugBtn}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onPick(p.code);
                setText(null);
              }}
            >
              <b>{p.code}</b>
              <span>{p.name}</span>
              <span>{p.country}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Nguồn `p2pBuyRate` riêng, chỉ dựng (và chỉ tải) khi có quyền `money`; không đi chung với giá bán. */
function BuyFeed({ onData }: { onData: (m: ReadonlyMap<string, P2PBuyRate>) => void }) {
  const buys = useList('p2pBuyRate');
  const items = buys.data?.items;
  useEffect(() => {
    if (items) onData(new Map(items.map((b) => [b.id, b])));
  }, [items, onData]);
  return null;
}

type View = 'T1' | 'T2' | 'COST' | 'FREE';
interface Marg {
  m20: number;
  m40: number;
}
const sign = (n: number) => (n >= 0 ? '+' : '−') + usd(Math.abs(n)).replace(/^[−-]/, '');

/** Giá hiện trên thẻ theo tab: bậc 1 / bậc 2 (giá bán), giá mua, hoặc giá mua + margin tự đặt (board `Mint-P2P`, `money=view`). */
function priceOf(r: P2PRate, size: '20' | '40', view: View, buy: P2PBuyRate | undefined, marg: Marg): number {
  const b = buy && (size === '20' ? buy.buy20 : buy.buy40);
  if (view === 'COST' && b !== undefined) return b;
  if (view === 'FREE' && b !== undefined) return b + (size === '20' ? marg.m20 : marg.m40);
  return sellOf(r, size, view === 'T1' ? 1 : 2);
}

function RateCard({ hit, view, buy, marg, sailings }: { hit: RateHit; view: View; buy: P2PBuyRate | undefined; marg: Marg; sailings: readonly Sailing[] }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const r = hit.rate;
  const prices = (['20', '40'] as const).map((k) => {
    const sell = priceOf(r, k, view, buy, marg);
    const cost = buy && (k === '20' ? buy.buy20 : buy.buy40);
    return { k, sell, cost, low: cost !== undefined && view !== 'COST' && sell < cost };
  });
  return (
    <article className={s.card} aria-label={t('p2.rateOf', { v: r.carrierName })}>
      <div className={s.cardMain}>
        <div className={s.car}>
          <div className={s.carTop}>
            <span className={s.carCode}>{r.carrier}</span>
            <span className={s.carName}>{r.carrierName}</span>
            {hit.cheapest && (
              <Chip tone="ok" label={t('p2.cheapestTip')}>
                {t('p2.cheapest')}
              </Chip>
            )}
          </div>
          <span className={s.sub}>{r.service ?? t('p2.svcUnknown')} · {r.channel}</span>
        </div>
        <div className={s.axis} role="img" aria-label={`${t('p2.axis', { a: r.pol, b: r.pod, n: r.transitDays })}${r.via ? `, ${t('p2.ts', { v: r.via })}` : `, ${t('p2.directLow')}`}`}>
          <div>
            <b>{r.pol}</b>
            <small>{cityOf(r.pol)}</small>
          </div>
          <div className={s.line}>
            <b>{t('p2.days', { n: r.transitDays })}</b>
            <i />
            <span>{r.via ?? t('p2.directUp')}</span>
          </div>
          <div>
            <b>{r.pod}</b>
            <small>{cityOf(r.pod)}</small>
          </div>
        </div>
        <div className={s.prices}>
          {prices.map(({ k, sell, cost, low }) => (
            <div key={k} className={s.price}>
              <small>{k}&apos;</small>
              <b>{usd(sell)}</b>
              {cost !== undefined && view !== 'COST' && (
                <span className={low ? s.low : undefined}>{t('p2.buy', { v: usd(cost) })} · {sign(sell - cost)}</span>
              )}
            </div>
          ))}
          {prices.some((p) => p.low) && (
            <Chip tone="warn" label={t('p2.belowTip')}>
              {t('p2.below')}
            </Chip>
          )}
        </div>
        <div className={s.valid}>
          <Chip tone={hit.validity === 'expiring' ? 'warn' : hit.validity === 'upcoming' ? 'info' : 'neutral'} icon={<IconClock size={13} />} label={t('p2.validUntil')}>
            {dm(hit.rate.validEnd)} · {daysLeftLabel(hit.daysLeft)}
          </Chip>
        </div>
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={open}
          aria-label={t('p2.detail', { v: r.carrierName })}
          icon={open ? <IconChevronUp size={18} /> : <IconChevronDown size={18} />}
          onClick={() => setOpen(!open)}
        />
      </div>
      {open && (
        <div className={s.detail}>
          <div className={s.meta}>
            <Chip icon={<IconPlugConnected size={13} />} label={t('p2.free')}>
              {freetimeLabel(r.freetime)}
            </Chip>
            <Chip icon={<IconShip size={13} />} label={t('p2.next')}>
              {r.nextVoyage} · ETD {dm(r.etd)}
            </Chip>
            <Chip icon={<IconClock size={13} />} label={t('p2.cy')}>
              {dmHm(r.cyCutoff)}
            </Chip>
          </div>
          {sailings.length > 0 && (
            <ul className={s.history} aria-label={t('p2.carrierSched')}>
              {sailings.slice(0, 3).map((v) => (
                <li key={v.id}>
                  {v.vessel} {v.voyage} · ETD {dm(v.etd)} → ETA {dm(v.eta)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </article>
  );
}

const schedCols = (t: (key: string) => string): Column<Sailing>[] => [
  { key: 'car', header: t('p2.hCarrier'), width: 80, cell: (v) => <b>{v.carrier}</b> },
  { key: 'ves', header: t('p2.hVessel'), cell: (v) => `${v.vessel} ${v.voyage}` },
  { key: 'svc', header: 'Service', width: 90, minor: true, cell: (v) => v.service ?? '—' },
  { key: 'etd', header: 'ETD', width: 80, cell: (v) => dm(v.etd) },
  { key: 'eta', header: 'ETA', width: 80, minor: true, cell: (v) => dm(v.eta) },
  { key: 'tt', header: 'T/T', width: 70, align: 'right', minor: true, cell: (v) => `${v.transitDays}d` },
  { key: 'cy', header: t('p2.hCut'), width: 130, cell: (v) => dmHm(v.cyCutoff) },
  { key: 'term', header: 'Terminal', minor: true, cell: (v) => v.terminal },
];

/** Điện thoại: lịch tàu là danh sách thẻ chuyến (board Mint-P2P-Phone · "Lịch tàu · N"), không phải bảng. */
function SailingCard({ v }: { v: Sailing }) {
  const t = useT();
  return (
    <article className={s.vcard} aria-label={t('p2.sailing', { v: `${v.vessel} ${v.voyage}` })}>
      <div className={s.vTop}>
        <div className={s.vDate}>
          <small>ETD</small>
          <b>{dm(v.etd)}</b>
        </div>
        <div className={s.vAxis} role="img" aria-label={`${t('p2.days', { n: v.transitDays })}, ${v.via ? t('p2.via', { v: v.via }) : t('p2.directLow')}`}>
          <b>{t('p2.days', { n: v.transitDays })}</b>
          <i />
          <span>{v.via ? t('p2.via', { v: v.via }) : t('p2.directUp')}</span>
        </div>
        <div className={`${s.vDate} ${s.vEnd}`}>
          <small>ETA</small>
          <b>{dm(v.eta)}</b>
        </div>
      </div>
      <div className={s.vCities}>
        <span>{cityOf(v.pol).toUpperCase()}</span>
        <span>{cityOf(v.pod).toUpperCase()}</span>
      </div>
      <div className={s.vMeta}>
        <span><IconShip size={14} aria-hidden="true" /><b>{v.vessel} {v.voyage}</b></span>
        <span className={s.sub}>{v.service ?? t('p2.unmapped')}{v.teuHeld > 0 ? ` · ${v.teuHeld} TEU` : ''}</span>
        <span className={s.vCut}>{t('p2.cutCy')} <b>{dmHm(v.cyCutoff)}</b></span>
        <span className={s.vCar}>{v.carrier}</span>
      </div>
      {/* TODO: Book mở Booking khi luồng D có route nhận chuyến. */}
      <Button variant="primary" icon={<IconCalendarCheck size={16} />} aria-label={t('p2.book', { v: `${v.vessel} ${v.voyage}` })}>
        Book
      </Button>
    </article>
  );
}

const SORTS = [
  { key: 'PRICE', label: 'p2.sPrice' },
  { key: 'TT', label: 'p2.sTt' },
  { key: 'VALID', label: 'p2.sValid' },
] as const;

/** Màn Tra giá & lịch tàu point-to-point (board Mint-P2P · iPad · Phone · Sched · 1920): tra đơn giản trước, ra kết quả sau. */
export function P2PScreen() {
  const t = useT();
  const access = useP2PAccess();
  const layout = useLayout();
  const phone = layout === 'phone';
  const pad = layout === 'pad';
  const rates = useList('p2pRate');
  const sails = useList('sailing');
  const [form, setForm] = useState<Lane>({ pol: 'VNSGN', pod: 'KRPUS', cargo: 'RF', days: 28 });
  const [lane, setLane] = useState<Lane | null>(null);
  const [tab, setTab] = useState<'rate' | 'sched'>('rate');
  const [carrier, setCarrier] = useState<string | null>(null);
  const [sort, setSort] = useState<RateSort>('PRICE');
  const [view, setView] = useState<View>('T2');
  const [marg, setMarg] = useState<Marg>({ m20: 50, m40: 80 });
  const [margOpen, setMargOpen] = useState(false);
  const [fltOpen, setFltOpen] = useState(false);
  const [buys, setBuys] = useState<ReadonlyMap<string, P2PBuyRate>>(new Map());
  const tier: SellTier = view === 'T1' ? 1 : 2;

  const result = useMemo(
    () => {
      if (!lane || !rates.data) return null;
      const items = rates.data.items as P2PRate[];
      // Giá mua / giá mua + margin: đổi cột bán của dòng giá để sắp xếp và "Rẻ nhất" tính trên đúng giá đang xem.
      const shown =
        access.money && (view === 'COST' || view === 'FREE')
          ? items.map((r) => ({ ...r, sell20: priceOf(r, '20', view, buys.get(r.id), marg), sell40: priceOf(r, '40', view, buys.get(r.id), marg) }))
          : items;
      return searchRates(shown, { ...lane, today: TODAY, carrier, sort, tier });
    },
    [lane, rates.data, carrier, sort, tier, view, buys, marg, access.money],
  );
  const sched = useMemo(
    () => (lane && sails.data ? searchSailings(sails.data.items as Sailing[], { ...lane, today: TODAY, days: lane.days, carrier }) : []),
    [lane, sails.data, carrier],
  );

  const schedAll = useMemo(
    () => (lane && sails.data ? searchSailings(sails.data.items as Sailing[], { ...lane, today: TODAY, days: lane.days }) : []),
    [lane, sails.data],
  );
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [qpick, setQpick] = useState<ReadonlySet<string>>(new Set());
  const padRes = (pad || phone) && !!lane && tab === 'rate';
  const wideSched = tab === 'sched' && !phone && !pad;
  /** Desktop + đã tra giá: thanh điều kiện một hàng, kết quả trong một thẻ, thẻ giá như board (Mint-P2P · -1920, bước kết quả). */
  const deskRes = !phone && !pad && !!lane && tab === 'rate';
  const cardRes = padRes || deskRes;

  const failed = rates.error ?? sails.error;
  let state: ScreenState = 'ready';
  if (access.force) state = access.force;
  else if (rates.isPending || sails.isPending) state = 'loading';
  else if (failed) state = failed instanceof DataError && failed.code === 'noperm' ? 'noperm' : 'error';
  else if (lane && !(result?.hits.length || sched.length)) state = 'empty';

  const laneK = lane ? `${lane.pol} → ${lane.pod}` : undefined;
  const swap = () => setForm({ ...form, pol: form.pod, pod: form.pol });
  const run = () => {
    setLane(form);
    setCarrier(null);
  };
  const retry = () => void Promise.all([rates.refetch(), sails.refetch()]);
  const pickRecent = (l: Lane) => {
    setForm(l);
    setLane(l);
    setCarrier(null);
  };

  const phoneSearch = (
    <section className={s.pSearch} aria-label={t('p2.cond')}>
      <p className={s.pFrom}>{t('p2.from')} <b>{form.pol} · {cityOf(form.pol).toUpperCase()}</b> {t('p2.to')}</p>
      <div className={s.pPods} role="group" aria-label={t('p2.pod')}>
        {PHONE_PODS.map((c) => (
          <button key={c} type="button" className={s.pPod} aria-pressed={form.pod === c} onClick={() => setForm({ ...form, pod: c })}>
            {c}
          </button>
        ))}
      </div>
      <Segmented
        kind="toggle"
        label={t('p2.cargoType')}
        value={form.cargo === 'RF' ? 'RF' : 'DC'}
        onChange={(cargo) => setForm({ ...form, cargo })}
        items={[{ key: 'DC', label: t('p2.dc') }, { key: 'RF', label: t('p2.rf') }]}
      />
      <Button variant="primary" icon={<IconSearch size={16} />} onClick={run} disabled={!form.pol || !form.pod || form.pol === form.pod}>
        {t('p2.search')}
      </Button>
    </section>
  );

  // Điện thoại đã tra: board chỉ giữ hàng đổi loại hàng trên danh sách kết quả, không dựng lại cả form.
  const pods = lane && (
    <div className={s.pPods} role="group" aria-label={t('p2.pod')}>
      {PHONE_PODS.map((c) => (
        <button
          key={c}
          type="button"
          className={s.pPod}
          aria-pressed={lane.pod === c}
          onClick={() => {
            setForm({ ...form, pod: c });
            setLane({ ...lane, pod: c });
            setCarrier(null);
          }}
        >
          {c}
        </button>
      ))}
    </div>
  );
  const cargoSeg = lane && (
    <Segmented
      kind="toggle"
      label={t('p2.cargoType')}
      value={lane.cargo === 'RF' ? 'RF' : 'DC'}
      onChange={(cargo) => {
        setForm({ ...form, cargo });
        setLane({ ...lane, cargo });
      }}
      items={[{ key: 'DC', label: t('p2.dc') }, { key: 'RF', label: t('p2.rf') }]}
    />
  );
  const phoneBar = lane && (
    <>
      {pods}
      {cargoSeg}
    </>
  );

  // iPad đã tra: điều kiện thu thành một hàng (POL → các POD) + loại hàng; không dựng lại cả form (board Mint-P2P-iPad, bước kết quả).
  const padCond = lane && (
    <section className={s.pdCond} aria-label={t('pd.condAria')}>
      <div className={s.pdRow}>
        <span className={s.pdPol}>{lane.pol} · {cityOf(lane.pol).toUpperCase()}</span>
        <span aria-hidden="true" className={s.pdArrow}>→</span>
        {PHONE_PODS.map((c) => (
          <button
            key={c}
            type="button"
            className={s.pdPod}
            aria-pressed={lane.pod === c}
            onClick={() => {
              setForm({ ...form, pod: c });
              setLane({ ...lane, pod: c });
              setCarrier(null);
            }}
          >
            <b>{c}</b> {cityOf(c).toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase())}
          </button>
        ))}
      </div>
      {cargoSeg}
    </section>
  );

  // iPad chưa tra: một thẻ giữa trang — POL → các POD dạng chip, loại hàng + nút Tra giá một hàng (board Mint-P2P-iPad).
  const padSearch = (
    <section className={`${s.pdCond} ${s.pdStart}`} aria-label={t('p2.recentAria')}>
      <div className={s.pdRow}>
        <span className={s.pdPol}>{form.pol} · {cityOf(form.pol).toUpperCase()}</span>
        <span aria-hidden="true" className={s.pdArrow}>→</span>
        {PHONE_PODS.map((c) => (
          <button key={c} type="button" className={s.pdPod} aria-pressed={form.pod === c} onClick={() => pickRecent({ ...form, pod: c })}>
            <b>{c}</b> {cityOf(c).toLowerCase().replace(/(^|\s)\S/g, (m) => m.toUpperCase())}
          </button>
        ))}
      </div>
      <div className={s.pdAct}>
        <Segmented
          kind="toggle"
          label={t('p2.cargoType')}
          value={form.cargo === 'RF' ? 'RF' : 'DC'}
          onChange={(cargo) => setForm({ ...form, cargo })}
          items={[{ key: 'DC', label: t('p2.dc') }, { key: 'RF', label: t('p2.rf') }]}
        />
        <Button variant="primary" icon={<IconSearch size={16} />} onClick={run} disabled={!form.pol || !form.pod || form.pol === form.pod}>
          {t('p2.search')}
        </Button>
      </div>
    </section>
  );

  const searching = (
    <section className={`${s.search} ${lane ? '' : s.searchStart}`} aria-label={t('p2.cond')}>
      {!lane && (
        <div className={s.lead}>
          <h2>{t('p2.lead')}</h2>
        </div>
      )}
      <div className={`${s.row} ${wideSched ? s.rowSched : ''} ${deskRes ? s.rowOne : ''}`}>
        <div className={s.polSwap}>
          <PortField label={t('p2.polL')} aria={t('p2.pol')} value={form.pol} onPick={(pol) => setForm({ ...form, pol })} />
          <Button className={s.swap} variant="ghost" aria-label={t('p2.swap')} icon={<IconArrowsExchange size={18} />} onClick={swap} />
        </div>
        <PortField label={t('p2.podL')} aria={t('p2.pod')} value={form.pod} onPick={(pod) => setForm({ ...form, pod })} />
        <div className={s.ctl}>
          <span className={s.ctlLb}><IconCalendarEvent size={14} aria-hidden="true" />{t('p2.etdIn')}</span>
          <Segmented
            kind="toggle"
            label={t('p2.etdRange')}
            value={String(form.days) as '14' | '28' | '42'}
            onChange={(k) => setForm({ ...form, days: Number(k) })}
            items={[{ key: '14', label: t('p2.w2') }, { key: '28', label: t('p2.w4') }, { key: '42', label: t('p2.w6') }]}
          />
        </div>
        {deskRes && <CargoField value={form.cargo} onPick={(cargo) => setForm({ ...form, cargo })} />}
        {deskRes && (
          <Button className={s.runRes} variant="primary" icon={<IconSearch size={16} />} onClick={run} disabled={!form.pol || !form.pod || form.pol === form.pod}>
            {t('p2.search')}
          </Button>
        )}
        {wideSched && (
          <Button className={s.runSched} variant="primary" icon={<IconSearch size={16} />} onClick={run} disabled={!form.pol || !form.pod || form.pol === form.pod}>
            {t('p2.searchSched')}
          </Button>
        )}
      </div>
      {wideSched || deskRes ? null : (
        <div className={s.row2}>
          <CargoField value={form.cargo} onPick={(cargo) => setForm({ ...form, cargo })} />
          <Button variant="primary" icon={<IconSearch size={16} />} onClick={run} disabled={!form.pol || !form.pod || form.pol === form.pod}>
            {t('p2.search')}
          </Button>
        </div>
      )}
    </section>
  );

  const codes = (lane?.cargo === 'RF' ? ['20RF', '40RH'] : lane?.cargo === 'OTH' ? ['20FR', '40FR'] : ['20DC', '40DC']) as unknown as readonly [string, string];
  const rateBody = (
    <StateView
      state={state === 'loading' || state === 'error' || state === 'empty' || state === 'noperm' ? state : 'ready'}
      title={state === 'empty' ? t('p2.emptyT') : state === 'noperm' ? t('p2.nopermT') : undefined}
      desc={state === 'empty' ? t('p2.emptyD') : state === 'noperm' ? t('p2.nopermD') : state === 'error' ? t('p2.errD') : undefined}
      action={state === 'error' ? <Button onClick={retry}>{t('p2.retry')}</Button> : undefined}
      skeleton={
        <div className={s.skel}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={s.skelRow} />
          ))}
        </div>
      }
    >
      {result && (
        <div className={deskRes ? s.resCard : s.resPlain}>
          <div className={s.bar}>
            {!padRes && <div className={s.chips} role="group" aria-label={t('p2.byCarrier')}>
              <button type="button" className={s.chipBtn} aria-pressed={!carrier} onClick={() => setCarrier(null)}>
                {t('p2.allCarriers')} <span>{result.hits.length}</span>
              </button>
              {result.carriers.map((c) => (
                <button key={c} type="button" className={s.chipBtn} aria-pressed={carrier === c} onClick={() => setCarrier(carrier === c ? null : c)}>
                  {c} <span>{result.hits.filter((h) => h.rate.carrier === c).length}</span>
                </button>
              ))}
            </div>}
            {padRes && pad && <span className={s.pdMeta}>{t('pd.meta', { n: result.carriers.length, c: codes.join(', ') })}</span>}
            {!padRes && <span className={s.grow} />}
            {!(padRes && phone) && <Segmented
              kind="toggle"
              label={t('p2.tier')}
              value={view}
              onChange={(k) => (k === 'FREE' ? setMargOpen(true) : setView(k))}
              items={[
                { key: 'T1', label: t('p2.t1') },
                { key: 'T2', label: t('p2.t2') },
                ...(access.money ? [{ key: 'COST' as const, label: t('p2.cost') }, { key: 'FREE' as const, label: t('p2.free2') }] : []),
              ]}
            />}
            {access.money && view === 'FREE' && (
              <Button variant="secondary" size="sm" icon={<IconPencil size={14} />} aria-label={t('p2.editMarg')} onClick={() => setMargOpen(true)}>
                {t('p2.marg')} {marg.m20 >= 0 ? '+' : ''}{marg.m20} / {marg.m40 >= 0 ? '+' : ''}{marg.m40}
              </Button>
            )}
            <div className={s.flt}>
              <Button variant="soft" size="sm" aria-haspopup="dialog" aria-expanded={fltOpen} title={t('p2.filterRes')} icon={<IconFilter size={16} aria-hidden="true" />} onClick={() => setFltOpen(!fltOpen)}>
                {t('p2.filter')}{sort !== 'PRICE' ? ' · 1' : ''} ▾
              </Button>
              {fltOpen && (
                <div className={s.pop} role="dialog" aria-label={t('p2.filterRes')}>
                  <div className={s.grp}>
                    <span>{t('p2.sort')}</span>
                    <Segmented kind="toggle" label={t('p2.sort')} value={sort} onChange={setSort} items={SORTS.map((o) => ({ ...o, label: t(o.label) }))} />
                  </div>
                  <div className={s.popFoot}>
                    <Button variant="ghost" size="sm" icon={<IconRestore size={16} aria-hidden="true" />} onClick={() => setSort('PRICE')}>{t('p2.reset')}</Button>
                    <span className={s.grow}>{t('p2.validRows', { n: result.hits.length })}</span>
                    <Button variant="secondary" size="sm" icon={<IconCheck size={16} aria-hidden="true" />} onClick={() => setFltOpen(false)}>{t('p2.done')}</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <section className={s.list} aria-label={t('p2.results')}>
            {cardRes ? result.hits.map((h) => {
              const buy = access.money ? buys.get(h.rate.id) : undefined;
              return (
                <PadRateCard
                  key={h.rate.id}
                  hit={h}
                  sell={[priceOf(h.rate, '20', view, buy, marg), priceOf(h.rate, '40', view, buy, marg)]}
                  cost={buy && view !== 'COST' ? buy : undefined}
                  caption={t('pd.cap', { v: t(view === 'T1' ? 'pd.capT1' : view === 'COST' ? 'pd.capCost' : view === 'FREE' ? 'pd.capFree' : 'pd.capT2') })}
                  codes={codes}
                  picked={qpick.has(h.rate.id)}
                  onPick={() => setQpick((p) => { const n = new Set(p); if (!n.delete(h.rate.id)) n.add(h.rate.id); return n; })}
                  sailings={sched.filter((v) => v.carrier === h.rate.carrier)}
                  desk={deskRes}
                />
              );
            }) : result.hits.map((h) => (
              <RateCard key={h.rate.id} hit={h} view={view} buy={access.money ? buys.get(h.rate.id) : undefined} marg={marg} sailings={sched.filter((v) => v.carrier === h.rate.carrier)} />
            ))}
          </section>
          {result.expired.length > 0 && (
            <p className={s.note}>
              {t('p2.expiredNote')}: {result.expired.map((e) => `${e.carrier} ×${e.rates.length}`).join(' · ')}
            </p>
          )}
        </div>
      )}
    </StateView>
  );

  const schedBody = (
    <StateView
      state={state === 'loading' || state === 'error' ? state : sched.length === 0 && state !== 'noperm' ? 'empty' : 'ready'}
      title={t('p2.noSailT')}
      desc={t('p2.noSailD')}
      action={state === 'error' ? <Button onClick={retry}>{t('p2.retry')}</Button> : undefined}
    >
      {wideSched ? (
        <SchedList sails={schedAll} now={ms(`${TODAY}T00:00:00+07:00`) - 14 * 3600000} picked={picked} onPick={setPicked} />
      ) : phone ? (
        <div className={s.list} role="list" aria-label={`${t('p2.sched')} · ${sched.length}`}>
          <h3 className={s.vHead}>{t('p2.sched')} · {sched.length}</h3>
          {sched.map((v) => (
            <SailingCard key={v.id} v={v} />
          ))}
        </div>
      ) : (
        <div className={s.sched}>
          <Table columns={schedCols(t)} rows={sched} rowKey={(v) => v.id} label={t('p2.sched')} dense />
        </div>
      )}
    </StateView>
  );

  const tabsEl = <Segmented label={t('p2.what')} value={tab} onChange={setTab} items={[{ key: 'rate', label: t('p2.search') }, { key: 'sched', label: t('p2.sched') }]} />;

  return (
    <>
      <Page>
      <PageHeader
        title={phone ? t('p2.search') : t('p2.title')}
        sub={wideSched || deskRes ? undefined : (phone || pad) && !laneK ? `${form.pol} → ${form.pod} · ${form.cargo}` : laneK ? `${laneK} · ${cargoLabel(lane!.cargo)} · ETD ${t('p2.weeks', { n: lane!.days / 7 })}` : undefined}
        tabs={tabsEl}
        lead={padRes ? (
          <HeaderTool label={t('pd.back')} icon={IconChevronLeft} onClick={() => { setLane(null); setCarrier(null); setQpick(new Set()); }} />
        ) : undefined}
        actions={
          phone ? (
            tabsEl
          ) : pad ? (
            padRes ? (
              <>
                <HeaderTool label={t('p2.more')} icon={IconDots} />
                <Button variant="primary" aria-disabled={qpick.size === 0} title={t('pd.quoteTip')}>{t('pd.quoteN', { n: qpick.size })}</Button>
              </>
            ) : undefined
          ) : (
            <>
              {wideSched && (
                <Button size="sm" variant="secondary" aria-disabled={picked.size === 0} icon={<IconCalendarCheck size={16} aria-hidden="true" />} title={picked.size ? t('p2.bookN', { n: picked.size }) : t('p2.tickBook')}>
                  Multi Book · {picked.size}
                </Button>
              )}
              <HeaderTool label={t('p2.recentTool')} icon={IconHistory} />
              <HeaderTool label={t('p2.more')} icon={IconDots} />
              {lane && !wideSched && (
                <Button size="sm" variant="primary" aria-disabled={qpick.size === 0} title={t('p2.tickQuote')} icon={<IconFileText size={16} aria-hidden="true" />}>
                  {t('p2.quote')} · {qpick.size}
                </Button>
              )}
            </>
          )
        }
      />
      <div className={s.page}>
        {phone ? (lane ? phoneBar : phoneSearch) : padRes ? padCond : pad && !lane ? padSearch : searching}
        {lane ? (
          <>
            {tab === 'rate' ? rateBody : schedBody}
          </>
        ) : (
          <>
            {!pad && <section className={`${s.recent} ${s.recentStart}`} aria-label={t('p2.recentAria')}>
              <h3>{t('p2.recent')}</h3>
              <div className={s.recentGrid}>
                {RECENT.map((l) => (
                  <button key={`${l.pol}${l.pod}${l.cargo}`} type="button" className={s.recentBtn} onClick={() => pickRecent(l)}>
                    <b>{l.pol} → {l.pod}</b>
                    <span>{l.cargo} · {l.at}</span>
                  </button>
                ))}
              </div>
            </section>}
            {state !== 'ready' && state !== 'empty' && <StateView state={state} />}
          </>
        )}
        {padRes && phone && (
          <div className={s.pdDock}>
            <Button icon={<IconPencil size={16} aria-hidden="true" />}>{t('pd.compose')}</Button>
            <Button variant="primary" aria-disabled={qpick.size === 0} title={t('pd.quoteTip')} icon={<IconFileText size={16} aria-hidden="true" />}>{t('pd.quoteN', { n: qpick.size })}</Button>
          </div>
        )}
      </div>
      </Page>
      {access.money && <BuyFeed onData={setBuys} />}
      {access.money && (
        <MargModal
          open={margOpen}
          marg={marg}
          onClose={() => setMargOpen(false)}
          onApply={(m) => {
            setMarg(m);
            setView('FREE');
            setMargOpen(false);
          }}
        />
      )}
    </>
  );
}

/** Modal "Tự đặt margin" (board `Mint-P2P`, `money=view`): cộng trên giá mua từng hãng, có thể âm. */
function MargModal({ open, marg, onClose, onApply }: { open: boolean; marg: Marg; onClose: () => void; onApply: (m: Marg) => void }) {
  const t = useT();
  const [draft, setDraft] = useState({ m20: String(marg.m20), m40: String(marg.m40) });
  useEffect(() => {
    if (open) setDraft({ m20: String(marg.m20), m40: String(marg.m40) });
  }, [open, marg]);
  const num = (v: string) => Number(v) || 0;
  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={t('p2.free2')}
      description={t('p2.margD')}
      footer={
        <>
          <Button onClick={onClose}>{t('p2.cancel')}</Button>
          <Button variant="primary" onClick={() => onApply({ m20: num(draft.m20), m40: num(draft.m40) })}>
            {t('p2.apply')}
          </Button>
        </>
      }
    >
      <Input label="Margin 20' (USD)" inputMode="numeric" value={draft.m20} onChange={(e) => setDraft({ ...draft, m20: e.target.value })} />
      <Input label="Margin 40' (USD)" inputMode="numeric" value={draft.m40} onChange={(e) => setDraft({ ...draft, m40: e.target.value })} />
    </Modal>
  );
}
